import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Text } from '@mercai/clever'

import styles from './general-notes.module.css'
import {
  getNoteDisplay,
  ScoreVisualizationEnum,
} from './general-notes.constants'
import {
  GeneralNotesProps,
  ScoreDisplayProps,
  IndividualScoresProps,
} from './general-notes.type'

const ScoreDisplay = ({
  title,
  note,
  visualizationType,
}: ScoreDisplayProps) => {
  const { t: tEnums } = useTranslation('enums')

  const noteData = getNoteDisplay(note)

  switch (visualizationType) {
    case 'emoji':
      return (
        <div className={styles['horizontal-score-item']}>
          <Text variant="text-xsmall" className={styles['score-title']}>
            {title}
          </Text>
          <div className={styles['emoji-display']}>
            <Text variant="text-large">{noteData.emoji}</Text>
            <Text variant="text-small">
              {tEnums(`general_notes_evaluation_${noteData.text || ''}`)}
            </Text>
          </div>
        </div>
      )

    case 'progress':
      return (
        <div className={styles['horizontal-score-item']}>
          <Text variant="text-xsmall" className={styles['score-title']}>
            {title}
          </Text>
          <div className={styles['progress-display']}>
            <div className={styles['progress-bar']}>
              <div
                className={styles['progress-fill']}
                style={{ width: `${(note / 5) * 100}%` }}
              ></div>
            </div>
            <Text variant="text-small">{((note / 5) * 100).toFixed(0)}%</Text>
          </div>
        </div>
      )

    case 'stars':
      return (
        <div className={styles['horizontal-score-item']}>
          <Text variant="text-xsmall" className={styles['score-title']}>
            {title}
          </Text>
          <div className={styles['stars-display']}>
            <Text variant="text-small">{noteData.stars}</Text>
          </div>
        </div>
      )

    default:
      return null
  }
}

const IndividualScores = ({
  evaluation,
  visualizationType,
}: IndividualScoresProps) => {
  const { t } = useTranslation('screenAiCenter')

  return (
    <div className={styles['horizontal-scores']}>
      <ScoreDisplay
        title={t('conversationShowGeneralNotesClarity')}
        note={evaluation?.clarityNote || 0}
        visualizationType={visualizationType}
      />

      <ScoreDisplay
        title={t('conversationShowGeneralNotesResolution')}
        note={evaluation?.resolutionSuccessNote || 0}
        visualizationType={visualizationType}
      />

      <ScoreDisplay
        title={t('conversationShowGeneralNotesSentiment')}
        note={evaluation?.userSentimentNote || 0}
        visualizationType={visualizationType}
      />
    </div>
  )
}

export const GeneralNotes = ({
  evaluation,
  visualizationType: initialVisualizationType,
}: Omit<GeneralNotesProps, 'title'>) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenAiCenter')

  const [visualizationType, setVisualizationType] =
    useState<ScoreVisualizationEnum>(initialVisualizationType || 'progress')
  const generalNoteData = getNoteDisplay(
    Math.round(evaluation?.generalNote || 0),
  )

  const onChangeVisualizationType = () => {
    const nextVisualizationType = {
      emoji: 'progress',
      progress: 'stars',
      stars: 'emoji',
    }

    setVisualizationType(
      nextVisualizationType[visualizationType] as ScoreVisualizationEnum,
    )
  }

  const renderGeneralScoreDetails = () => {
    switch (visualizationType) {
      case 'emoji':
        return (
          <div className={styles['general-score-details']}>
            <Text variant="text-large" className={styles['score-emoji']}>
              {generalNoteData.emoji}
            </Text>
            <Text variant="text-medium" className={styles['score-text']}>
              {tEnums(`general_notes_evaluation_${generalNoteData.text}`)}
            </Text>
          </div>
        )

      case 'progress':
        return (
          <div className={styles['general-score-details']}>
            <Text variant="text-large" className={styles['score-emoji']}>
              {generalNoteData.emoji}
            </Text>
            <Text variant="text-medium" className={styles['score-text']}>
              {tEnums(`general_notes_evaluation_${generalNoteData.text}`)}
            </Text>
          </div>
        )

      case 'stars':
        return (
          <div className={styles['general-score-details']}>
            <Text variant="title-medium" className={styles['score-stars']}>
              {generalNoteData.stars}
            </Text>
            <Text variant="text-medium" className={styles['score-text']}>
              {tEnums(`general_notes_evaluation_${generalNoteData.text}`)}
            </Text>
          </div>
        )

      default:
        return null
    }
  }

  if (!evaluation?.generalNote)
    return (
      <div className={styles['score-section']}>
        <Text>{t('conversationShowGeneralNotesEmpty')}</Text>
      </div>
    )

  return (
    <div className={styles['score-section']}>
      <Text variant="title-medium">
        {t('conversationShowGeneralNotesSubtitle')}
      </Text>

      <div
        className={styles['general-score-display']}
        style={{
          background: generalNoteData.bgColor,
          borderColor: generalNoteData.borderColor,
        }}
      >
        <div className={styles['general-score-main']}>
          <Text
            variant="title-large"
            className={styles['score-number']}
            style={{
              color: generalNoteData.textColor,
            }}
          >
            {evaluation?.generalNote || 0}
          </Text>
          <Text variant="text-medium" className={styles['score-max']}>
            / 5
          </Text>
        </div>

        <button onClick={onChangeVisualizationType}>
          {renderGeneralScoreDetails()}
        </button>
      </div>

      <div
        className={styles.quote}
        style={{
          borderColor: generalNoteData?.borderColor,
        }}
      >
        <Text variant="text-medium">
          {evaluation?.generalNoteObservation || '-'}
        </Text>
      </div>

      <IndividualScores
        evaluation={evaluation}
        visualizationType={visualizationType || 'progress'}
      />
    </div>
  )
}
